import { useTranslation } from "react-i18next"

export interface TiktokScopeSummaryProps {
  /** i18n keys for individual scopes (BE-shipped via
   *  `scope_keys: string[]` on Step 3 + Settings props). FE resolves
   *  each via `t("tiktok.scopes.${key}")`. */
  scopeKeys: string[]
  /** Stable test selector (default `tiktok-connect-scope-summary` per
   *  UX Spec §2 frozen testid). */
  "data-testid"?: string
  className?: string
}

/**
 * TiktokScopeSummary — short consent line listing the OAuth scopes the
 * creator is granting. Rendered:
 *
 *   1. On OKS-22 Step 3 ("We'll access: …") — UX Spec §2.
 *   2. On Settings → Social Accounts (active state, read-only context).
 *
 * Scope keys come from BE (`scope_keys: string[]`) — never hard-coded
 * here. If TikTok adds a new scope, BE adds the i18n key to
 * `tiktok.scopes.*` and ships it; FE picks it up automatically.
 *
 * Renders nothing if the scope list is empty (defensive — BE always
 * ships at least the identity scope on a linked account).
 */
export function TiktokScopeSummary({
  scopeKeys,
  "data-testid": testId = "tiktok-connect-scope-summary",
  className = "",
}: TiktokScopeSummaryProps) {
  const { t } = useTranslation()
  if (scopeKeys.length === 0) return null
  const scopeLabels = scopeKeys.map((key) => t(`tiktok.scopes.${key}`))
  return (
    <p
      data-testid={testId}
      className={`text-muted-foreground text-sm ${className}`}
    >
      <span>{t("tiktok.scope_summary.lead")} </span>
      {scopeLabels.map((label, i) => (
        <span key={scopeKeys[i]} className="text-foreground">
          {label}
          {i < scopeLabels.length - 1 ? ", " : ""}
        </span>
      ))}
      <span>. {t("tiktok.scope_summary.no_post_disclaimer")}</span>
    </p>
  )
}

export default TiktokScopeSummary
